import { Icon, IContextualMenuItem, IIconStyles } from "@fluentui/react";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useSelector } from "react-redux";
import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { linkIconStyles } from "../../../src/taskpane/config/controlStyles";

const iconStyles: IIconStyles = {
    root: {
        display: "flex",
        marginLeft: 4,
        marginRight: 4
    }
}

const rootContentType: string = "root";

export enum DocumentTemplateTypes {
    'docx' = 1,
    'xlsx' = 5,
    'pptx' = 3,
    'one' = 4,
    'vsdx' = 8
}

export function useMenuItem_New(
    selectedItems: any[],
    waitFor: boolean,
    setIsCMCreateFolderPanelOpen: (isCMCreateFolderPanelOpen: boolean) => void,
    newDocumentTemplates: NewDocumentTemplate[],
    openCreateTemplateModal: (selectedTemplate: NewDocumentTemplate) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue?.value);
    const enableNewTemplateFilters = useSelector((state: RootState) => state.tenantInfo.enableNewTemplateFilters);
    const newTemplateDocumentSetFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateDocumentSetFilters);
    const newTemplateFolderFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateFolderFilters);
    const newTemplateLibraryFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateLibraryFilters);
    const enforceStrictDocumentSetsOnly = useSelector((state: RootState) => state.tenantInfo.enforceStrictDocumentSetsOnly);

    const [menuItem_New, setMenuItem_New] = React.useState<IContextualMenuItem>();

    const renderIcon = React.useCallback((template?: NewDocumentTemplate) => {
        if (newDocumentTemplates?.length > 0) {
            if (template?.templateId === "NewFolder" || !template) {
                return <Icon {...getFileTypeIconProps({ type: FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />;
            }
            if (template?.templateId === "NewLink" || !template) {
                return <Icon iconName="Globe" styles={linkIconStyles} />;
            }
            if (template?.isContentType === true && template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                return <Icon {...getFileTypeIconProps({ type: FileIconType.docset, size: 20, imageFileType: "svg" })} styles={iconStyles} />;
            }
            if (template?.isContentType === true && template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) {
                return <Icon {...getFileTypeIconProps({ type: FileIconType.linkedFolder, size: 20, imageFileType: "svg" })} styles={iconStyles} />;
            }
            if (template?.extention) {
                return <Icon {...getFileTypeIconProps({ extension: template.extention, size: 20, imageFileType: "svg" })} styles={iconStyles} />;
            }
        }
        return <Icon {...getFileTypeIconProps({ type: FileIconType.genericFile, size: 20, imageFileType: "svg" })} styles={iconStyles} />;
    }, [newDocumentTemplates, currentDirectory]);

    const newItem: IContextualMenuItem = {
        key: "new",
        disabled: !currentDirectory?.sharePointListId || waitFor === true || isBusySaving === true,
        text: languageStrings.NewOnly,
        iconProps: { iconName: "Add" },
        subMenuProps: {
            items: []
        }
    };

    React.useEffect(() => {
        const hasListInWorkingDir = workingDirectories.some(x => x.type === ComponentTypes.SharePointList);

        if (selectedItems?.length === 0 && currentSharepointFolder?.userPermissions?.canAddListItem && currentSharepointFolder?.userPermissions?.canEditListItem && !searchValue) {
            const isInFolder = ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));

            let currentContentype: string;
            if (enforceStrictDocumentSetsOnly || enableNewTemplateFilters) {
                if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                    currentContentype = SharePointFolderContentTypeIds.documentSet;
                } else if (isInFolder) {
                    currentContentype = SharePointFolderContentTypeIds.folder;
                } else {
                    currentContentype = rootContentType;
                }
            }

            var folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
            const canViewFolder = folderViewWrapper.canViewFolder();

            if (canViewFolder || (!canViewFolder && !isInFolder)) {
                let newTemplates: NewDocumentTemplate[];
                if (enforceStrictDocumentSetsOnly
                    && currentContentype === rootContentType
                    && newDocumentTemplates?.some(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                    newTemplates = newDocumentTemplates?.filter(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet));
                }
                else {
                    newTemplates = newDocumentTemplates;
                }

                newTemplates?.forEach(template => {
                    if (template.templateId === "NewFolder") {
                        if (currentSharepointFolder?.canCreateFolder && canViewFolder) {
                            const originalCreateFolder = {
                                key: "createFolder",
                                text: languageStrings.Folder,
                                iconProps: { iconName: "FabricNewFolder" },
                                onRenderIcon: () => renderIcon(),
                                onClick: () => {
                                    openCreateTemplateModal(template);
                                }
                            }
                            if (enableNewTemplateFilters) {
                                if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.folder)) {
                                    newItem.subMenuProps?.items?.push(originalCreateFolder);
                                }
                                else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.folder)) {
                                    newItem.subMenuProps?.items?.push(originalCreateFolder);
                                }
                                else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.folder)) {
                                    newItem.subMenuProps?.items?.push(originalCreateFolder);
                                }
                            } else {
                                newItem.subMenuProps?.items?.push(originalCreateFolder);
                            }
                        }
                    }
                    else if (!hasListInWorkingDir) {
                        const subMenuItem: IContextualMenuItem = {
                            key: `new-${template.templateId}`,
                            text: template.title,
                            iconProps: {},
                            onRenderIcon: () => renderIcon(template),
                            onClick: () => {
                                openCreateTemplateModal(template);
                            }
                        }

                        if (template.isContentType === true) {
                            if (template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && !template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)
                                || (template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet) && !workingDirectories?.some(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)))) {
                                if (enableNewTemplateFilters) {
                                    //folder template
                                    if ((template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && !template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                                        if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.folder)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.folder)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.folder)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        //document set template
                                    } else if (template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                                        if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.docset)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.docset)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.docset)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        //not docset or folder template
                                    } else if (!(template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && !template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) && !(template?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                                        if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.file)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.file)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                        else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.file)) {
                                            newItem.subMenuProps?.items?.push(subMenuItem);
                                        }
                                    }
                                } else {
                                    newItem.subMenuProps?.items?.push(subMenuItem);
                                }
                            }
                        }
                        else if (template.templateId !== 'NewXSLSurvey' && template.templateId !== 'NewXSLForm') {
                            if (enableNewTemplateFilters) {
                                if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.file)) {
                                    newItem.subMenuProps?.items?.push(subMenuItem);
                                }
                                else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.file)) {
                                    newItem.subMenuProps?.items?.push(subMenuItem);
                                }
                                else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.file)) {
                                    newItem.subMenuProps?.items?.push(subMenuItem);
                                }
                            } else {
                                newItem.subMenuProps?.items?.push(subMenuItem);
                            }
                        }
                    }
                });

                if (hasListInWorkingDir && selectedItems?.length === 0 && currentSharepointFolder?.userPermissions?.canAddListItem && currentSharepointFolder?.userPermissions?.canEditListItem && !searchValue) {
                    const originalCreateListItem = {
                        key: "createListItem",
                        text: languageStrings.ListItem,
                        iconProps: { iconName: "FabricNewFolder" },
                        onRenderIcon: () => <Icon {...getFileTypeIconProps({ type: FileIconType.listItem, size: 16, imageFileType: "svg" })} styles={iconStyles} />,
                        onClick: () => {
                            openCreateTemplateModal(null);
                        }
                    }

                    newItem.subMenuProps?.items?.push(originalCreateListItem);
                    setMenuItem_New(newItem);
                    return;
                }

                if (newItem.subMenuProps?.items?.length > 0) {
                    setMenuItem_New(newItem);
                    return;
                }
            }
        }

        setMenuItem_New(null);

    }, [selectedItems, waitFor, currentSharepointFolder, workingDirectories, currentDirectory, siteInfo, newDocumentTemplates, renderIcon, searchValue, enableNewTemplateFilters, newTemplateLibraryFilters, newTemplateFolderFilters, newTemplateDocumentSetFilters]);

    return {
        menuItem_New
    }
}